import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ProductsWrapper } from "../styled";
import {
  BackButton,
  Box,
  ButtonStyledIconLeft,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import { PlusIcon, SearchIcon } from "../../../../components/styled/AllImages";
import FloatingInput from "../../../../components/common/inputFields/FloatingInput";
import ListTable from "../../../../components/common/tables/ListTable";
import { productsListColumns } from "../../../../helpers/adminConstants/TableConstants";
import Breadcrumbs from "../../../../components/admin/Breadcrumbs/Breadcrumbs";
import DeleteModal from "../../../../components/admin/Modals/DeleteModal/DeleteModal";
import ProductsApi from "../../../../services/api/productsApi";
import { useDebounce } from "../../../../hooks/useDebounce";
import { toast } from "react-toastify";
import CategoriesApi from "../../../../services/api/categoriesApi";
import { getModuleOperations } from "../../../../utils";

const Products = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [productId, setProductId] = useState(null);
  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const onDelete = (row) => {
    setProductId(row.id);
    setShowDeleteModal(true);
  };

  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query);
  };

  const handleView = (product) => {
    navigate(`/products-categories/${id}/products/${product?.id}/view-product`);
  };

  const handleEdit = (product) => {
    navigate(`/products-categories/${id}/products/${product?.id}/edit-product`);
  };

  const getProductsList = async (page = 1, query = "") => {
    try {
      setLoading(true);
      const { data, code } = await ProductsApi.productsList(page, id, query);
      if (code === 200) {
        setRowsData(data?.data);
        setTotalCount(data?.total);
        setPerPage(data?.per_page);
      } else {
        setRowsData([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await ProductsApi.deleteProduct(id, productId);
      if (response.code === 200) {
        toast.success(t("product_deleted_successfully"));
        setShowDeleteModal(false);
        setProductId(null);
        getProductsList(activePage);
        dispatchEvent(new Event("productUpdated"))
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleStatusToggle = async (row) => {
    const newObj = {
      product_id: row.id,
      active: parseInt(row.active) === 1 ? 0 : 1,
    };
    try {
      const response = await ProductsApi.changeProductStatus(id, newObj);
      if (response.code === 200) {
        toast.success(t("product_status_updated"));
        getProductsList(activePage);
      } else {
        toast.error(response.message);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getProductsList(activePage, debouncedSearchQuery);
  }, [activePage, debouncedSearchQuery, language]);

  return (
    <ProductsWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("products")}
            onClick={() => navigate("/products-categories")}
          />
          <Breadcrumbs />
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          <FloatingInput
            name="search_value"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            bg="var(--white)"
            icon={SearchIcon}
            onChange={handleSearch}
          />

          {getModuleOperations(2, 1) && (
            <ButtonStyledIconLeft
              label={t("add_product")}
              icon={PlusIcon}
              onClick={() =>
                navigate(`/products-categories/${id}/products/add-product`)
              }
            />
          )}
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            options={["view", "edit", "status", "delete"]}
            typeId={2}
            rows={rowsData}
            columns={productsListColumns}
            onDelete={onDelete}
            onOpen={handleView}
            onEdit={handleEdit}
            onStatusToggle={handleStatusToggle}
            activePage={activePage}
            setActivePage={setActivePage}
            totalCount={totalCount}
            perPage={perPage}
          />
        )}
      </Box>
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        isLoading={loadingDelete}
        title={t("product")}
      />
    </ProductsWrapper>
  );
};

export default Products;
